import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { File, Loader2, Paperclip, Send, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { useAuth } from '@/lib/auth';
import { useSendSms } from '@/hooks/useSms';
import { useSendPatientMessage } from '@/hooks/usePatientMessages';
import { useAttachMessageDocument, validateMessageAttachmentFile } from '@/hooks/useMessageAttachments';
import { useUploadDocument } from '@/hooks/useDocuments';
import { useOwnEmailSignature, useSendPatientEmail } from '@/hooks/usePatientEmailThreads';
import { containsCpr } from '@/lib/clinicEmail/catalog';
import {
  EMAIL_ATTACHMENT_MAX_COUNT,
  fileToBase64,
  validateEmailComposeAttachment,
} from '@/lib/patientEmail';
import {
  type CommunicationChannel,
  type CommunicationFilter,
} from '@/lib/patientCommunication';
import {
  type MedcomTelephoneCode,
  type MedcomTelephoneSubscriber,
} from '@/lib/patientStamkortFields';
import { sendPatientMessageCompose } from '@/lib/sendPatientMessageCompose';
import { patientSmsSendBlockReason, smsPriceDkk, smsSegmentCount } from '@/lib/smsCompose';
import { FORM_LABEL_TYPE, TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export interface CommunicationComposeProps {
  clinicId: string;
  patientId: string;
  patientName: string;
  phoneNumbers: MedcomTelephoneSubscriber[];
  email: string;
  hasPortalAccount: boolean;
  consentAccepted: boolean;
  smsEnabled: boolean;
  hasCredit?: boolean;
  canSms: boolean;
  canEmail: boolean;
  canMessages: boolean;
  preferredChannel: CommunicationFilter;
  clinicEmailReady?: boolean;
  clinicSignature?: string;
  threadId?: string | null;
  onEmailSent?: () => void;
}

type ChannelAvailability = {
  available: boolean;
  reason: string | null;
};

function channelButtonClass(active: boolean, disabled: boolean) {
  return cn(
    TYPE.control,
    'rounded-full border px-s3 py-1',
    disabled && 'cursor-not-allowed opacity-50',
    active
      ? 'border-ds-teal bg-ds-teal-tint font-semibold text-ds-teal'
      : 'border-ds-field bg-ds-card font-medium text-ds-text',
  );
}

export function CommunicationCompose({
  clinicId,
  patientId,
  patientName,
  phoneNumbers,
  email,
  hasPortalAccount,
  consentAccepted,
  smsEnabled,
  hasCredit = true,
  canSms,
  canEmail,
  canMessages,
  preferredChannel,
  clinicEmailReady = false,
  clinicSignature = '',
  threadId = null,
  onEmailSent,
}: CommunicationComposeProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const sendSms = useSendSms();
  const sendMessage = useSendPatientMessage();
  const sendEmail = useSendPatientEmail();
  const { data: personalSignature = '' } = useOwnEmailSignature(clinicId, user?.id);
  const uploadDocument = useUploadDocument();
  const attachDocument = useAttachMessageDocument(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emailFileInputRef = useRef<HTMLInputElement>(null);
  const [selectedPhone, setSelectedPhone] = useState(phoneNumbers[0]?.identifier ?? '');
  const phone = selectedPhone || phoneNumbers[0]?.identifier || '';

  useEffect(() => {
    const identifiers = phoneNumbers.map((row) => row.identifier);
    setSelectedPhone((current) => {
      if (identifiers.length === 0) return '';
      if (identifiers.includes(current)) return current;
      return identifiers[0] ?? '';
    });
  }, [phoneNumbers]);

  const availability: Record<CommunicationChannel, ChannelAvailability> = {
    sms: smsAvailability(t, { canSms, smsEnabled, phone, consentAccepted, hasCredit }),
    email: emailAvailability(t, { canEmail, email, clinicEmailReady }),
    messages: messageAvailability(t, { canMessages, hasPortalAccount }),
  };

  const fallbackChannel = firstComposeChannel(availability, { canSms, canEmail, canMessages });
  const [channel, setChannel] = useState<CommunicationChannel>(
    () => resolveComposeChannel(preferredChannel, { canSms, canEmail, canMessages }, fallbackChannel),
  );
  const [body, setBody] = useState('');
  const [subject, setSubject] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [emailFiles, setEmailFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const signaturePreview = [personalSignature, clinicSignature].filter((s) => s.trim()).join('\n\n');

  useEffect(() => {
    setChannel(
      resolveComposeChannel(preferredChannel, { canSms, canEmail, canMessages }, fallbackChannel),
    );
    // Only follow the timeline filter chip; do not reset a manual channel pick.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- preferredChannel is the intentional trigger
  }, [preferredChannel]);

  const segments = useMemo(() => smsSegmentCount(body), [body]);
  const selected = availability[channel];

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles: File[] = [];
    for (const file of files) {
      const validationError = validateMessageAttachmentFile(file);
      if (validationError === 'size_limit_exceeded') {
        toast.error(t('documents.errors.size_limit', `Filen ${file.name} er for stor (max 50 MB)`));
      } else if (validationError === 'invalid_type') {
        toast.error(t('documents.errors.invalid_type', `Filtypen for ${file.name} er ikke tilladt`));
      } else {
        validFiles.push(file);
      }
    }
    setSelectedFiles((prev) => [...prev, ...validFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleEmailFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles: File[] = [];
    for (const file of files) {
      const validationError = validateEmailComposeAttachment(file);
      if (validationError === 'size') {
        toast.error(t('email.attachment_too_large', `Filen ${file.name} er for stor (max 10 MB)`));
      } else if (validationError === 'type') {
        toast.error(t('email.attachment_invalid_type', `Filtypen for ${file.name} er ikke tilladt`));
      } else {
        validFiles.push(file);
      }
    }
    setEmailFiles((prev) => [...prev, ...validFiles].slice(0, EMAIL_ATTACHMENT_MAX_COUNT));
    if (emailFileInputRef.current) emailFileInputRef.current.value = '';
  };

  const handleSendSms = async () => {
    const block = patientSmsSendBlockReason({
      body,
      phone,
      consentAccepted,
      smsEnabled,
      hasCredit,
    });
    if (block === 'empty') {
      toast.error(t('sms.body_required', 'SMS-tekst er påkrævet.'));
      return;
    }
    if (block === 'no_phone') {
      toast.error(t('sms.no_phone', 'Patienten har intet telefonnummer.'));
      return;
    }
    if (block === 'no_credit') {
      toast.error(t('sms.no_credit', 'Klinikken har ingen SMS-kredit. Køb en pakke under Indstillinger → SMS.'));
      return;
    }
    if (block) return;

    try {
      await sendSms.mutateAsync({
        clinic_id: clinicId,
        patient_id: patientId,
        subscriber_identifier: phone,
        body,
        trigger: 'manual',
        patient_name: patientName || undefined,
      });
      toast.success(t('sms.send_success', 'SMS sendt.'));
      setBody('');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('sms.send_failed', 'SMS-afsendelse fejlede.');
      toast.error(message);
    }
  };

  const handleSendMessage = async (e: FormEvent) => {
    e.preventDefault();
    if (!body.trim() && selectedFiles.length === 0) return;
    setSending(true);
    try {
      await sendPatientMessageCompose({
        patientId,
        content: body,
        files: selectedFiles,
        sendMessage: (params) => sendMessage.mutateAsync(params),
        uploadDocument: (params) => uploadDocument.mutateAsync(params),
        attachDocument: (params) => attachDocument.mutateAsync(params),
      });
      toast.success(t('messages.toasts.sent', 'Besked sendt'));
      setBody('');
      setSelectedFiles([]);
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error(t('messages.errors.send_failed', 'Kunne ikke sende besked'));
    } finally {
      setSending(false);
    }
  };

  const handleSendEmail = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error(t('email.compose_required', 'Emne og brødtekst er påkrævet.'));
      return;
    }
    if (containsCpr(subject) || containsCpr(body)) {
      toast.error(t('email.cpr_blocked', 'CPR må ikke indgå i e-mails.'));
      return;
    }
    setSending(true);
    try {
      const attachments = await Promise.all(
        emailFiles.map(async (file) => ({
          filename: file.name,
          content: await fileToBase64(file),
          contentType: file.type || undefined,
        })),
      );
      await sendEmail.mutateAsync({
        clinic_id: clinicId,
        patient_id: patientId,
        subject: subject.trim(),
        body,
        thread_id: threadId ?? undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
      });
      toast.success(t('email.send_success', 'E-mail sendt.'));
      setBody('');
      setSubject('');
      setEmailFiles([]);
      onEmailSent?.();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('email.send_failed', 'E-mail kunne ikke sendes.');
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (channel === 'sms') void handleSendSms();
    else if (channel === 'messages') void handleSendMessage(e);
    else if (channel === 'email') void handleSendEmail();
  };

  const visibleChannels = (['sms', 'email', 'messages'] as const).filter((key) => {
    if (key === 'sms') return canSms;
    if (key === 'email') return canEmail;
    return canMessages;
  });

  if (visibleChannels.length === 0) return null;

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-2xl bg-ds-card p-5 shadow-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className={cn(TYPE.label, 'text-ds-text')}>
          {t('patients.communication.compose_title', 'Skriv')}
        </div>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('patients.communication.channel', 'Kanal')}>
          {visibleChannels.map((key) => {
            const option = availability[key];
            return (
              <button
                key={key}
                type="button"
                title={option.reason ?? undefined}
                onClick={() => setChannel(key)}
                className={channelButtonClass(channel === key, !option.available)}
              >
                {channelLabel(t, key)}
              </button>
            );
          })}
        </div>
      </div>

      {selected.reason && (
        <p className={cn(TYPE.small, 'mt-3 text-ds-amber-ink')}>{selected.reason}</p>
      )}

      {channel === 'sms' && selected.available && (
        <div className="mt-4 space-y-3">
          {phoneNumbers.length > 1 && (
            <div>
              <label htmlFor="communication-sms-to" className={`block ${FORM_LABEL_TYPE}`}>
                {t('patients.communication.sms_to', 'Send til')}
              </label>
              <select
                id="communication-sms-to"
                value={phone}
                onChange={(event) => setSelectedPhone(event.target.value)}
                aria-label={t('patients.communication.sms_choose_number', 'Vælg telefonnummer')}
                className={cn(
                  'ds-field mt-1 block h-[44px] w-full',
                  TYPE.inputValue,
                )}
              >
                {phoneNumbers.map((row, index) => (
                  <option key={`${row.identifier}-${row.code}-${index}`} value={row.identifier}>
                    {row.identifier} ({telephoneTypeLabel(row.code, t)})
                  </option>
                ))}
              </select>
            </div>
          )}
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t('sms.compose_placeholder', 'Skriv en SMS til patienten…')}
            rows={3}
            maxLength={1000}
          />
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2 text-ds-muted">
              <Sparkles className="h-3 w-3" />
              <span>
                {body.length} {t('sms.chars', 'tegn')} · {segments}{' '}
                {segments === 1 ? t('sms.segment', 'segment') : t('sms.segments', 'segmenter')} ·{' '}
                {smsPriceDkk(segments)} DKK
              </span>
            </div>
            <Button type="submit" disabled={sendSms.isPending || !body.trim()} size="sm">
              {sendSms.isPending ? (
                <>
                  <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                  {t('sms.sending', 'Sender…')}
                </>
              ) : (
                <>
                  <Send className="mr-1.5 h-3 w-3" />
                  {t('sms.send_now', 'Send nu')}
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {channel === 'email' && selected.available && (
        <div className="mt-4 space-y-3">
          <Input
            name="communication-email-subject"
            label={t('email.subject', 'Emne')}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={300}
            autoComplete="off"
          />
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t('email.compose_placeholder', 'Skriv en e-mail til patienten…')}
            rows={6}
            maxLength={20000}
            disabled={sending}
          />
          {signaturePreview ? (
            <p className={cn(TYPE.small, 'whitespace-pre-wrap text-ds-muted')}>
              {t('email.signature_preview', 'Signatur (personlig + klinik)')}
              {':\n'}
              {signaturePreview}
            </p>
          ) : null}
          <input
            ref={emailFileInputRef}
            type="file"
            onChange={handleEmailFileSelect}
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt"
            className="hidden"
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => emailFileInputRef.current?.click()}
              disabled={sending || emailFiles.length >= EMAIL_ATTACHMENT_MAX_COUNT}
            >
              <Paperclip />
              {t('email.attach_file', 'Vedhæft fil')}
            </Button>
            <Button type="submit" disabled={sending || !subject.trim() || !body.trim()}>
              {sending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('email.sending', 'Sender…')}
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  {t('email.send_now', 'Send e-mail')}
                </>
              )}
            </Button>
          </div>
          {emailFiles.length > 0 && (
            <div className="space-y-2">
              {emailFiles.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="flex items-center justify-between rounded-md border border-ds-line bg-ds-sunk px-3 py-2"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <File className="h-4 w-4 flex-shrink-0 text-ds-muted" />
                    <span className="truncate text-sm text-ds-text">{file.name}</span>
                    <span className="flex-shrink-0 text-xs text-ds-muted">
                      ({Math.round(file.size / 1024)} KB)
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="danger"
                    size="icon"
                    className="ml-2 shrink-0"
                    onClick={() => setEmailFiles((prev) => prev.filter((_, i) => i !== index))}
                    disabled={sending}
                    aria-label={t('common.delete')}
                  >
                    <Trash2 />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {channel === 'messages' && selected.available && (
        <div className="mt-4 space-y-3">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t('messages.modal.placeholder', 'Skriv din besked her...')}
            rows={4}
            disabled={sending}
          />
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt"
            className="hidden"
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
            >
              <Paperclip />
              {t('messages.modal.attach_file', 'Vedhæft fil')}
            </Button>
            <Button type="submit" disabled={sending || (!body.trim() && selectedFiles.length === 0)}>
              {sending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('messages.modal.sending', 'Sender...')}
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  {t('messages.modal.send', 'Send')}
                </>
              )}
            </Button>
          </div>
          {selectedFiles.length > 0 && (
            <div className="space-y-2">
              {selectedFiles.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="flex items-center justify-between rounded-md border border-ds-line bg-ds-sunk px-3 py-2"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <File className="h-4 w-4 flex-shrink-0 text-ds-muted" />
                    <span className="truncate text-sm text-ds-text">{file.name}</span>
                    <span className="flex-shrink-0 text-xs text-ds-muted">
                      ({Math.round(file.size / 1024)} KB)
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="danger"
                    size="icon"
                    className="ml-2 shrink-0"
                    onClick={() => setSelectedFiles((prev) => prev.filter((_, i) => i !== index))}
                    disabled={sending}
                    aria-label={t('common.delete')}
                  >
                    <Trash2 />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </form>
  );
}

function telephoneTypeLabel(
  code: MedcomTelephoneCode,
  t: (key: string, fallback: string) => string,
): string {
  switch (code) {
    case 'fastnet':
      return t('patients.telephone_types.fastnet', 'Fastnet');
    case 'mobil':
      return t('patients.telephone_types.mobil', 'Mobil');
    case 'arbejde':
      return t('patients.telephone_types.arbejde', 'Arbejde');
    default: {
      const unknown: never = code;
      return unknown;
    }
  }
}

function channelLabel(
  t: (key: string, fallback: string) => string,
  channel: CommunicationChannel,
): string {
  switch (channel) {
    case 'sms':
      return t('patients.communication.filter_sms', 'SMS');
    case 'email':
      return t('patients.communication.filter_email', 'E-mail');
    case 'messages':
      return t('patients.communication.filter_messages', 'Beskeder');
    default: {
      const _exhaustive: never = channel;
      return _exhaustive;
    }
  }
}

function smsAvailability(
  t: (key: string, fallback: string) => string,
  opts: {
    canSms: boolean;
    smsEnabled: boolean;
    phone: string;
    consentAccepted: boolean;
    hasCredit: boolean;
  },
): ChannelAvailability {
  if (!opts.canSms) return { available: false, reason: null };
  if (!opts.smsEnabled) {
    return {
      available: false,
      reason: t('sms.not_enabled_title', 'SMS er ikke aktiveret for denne klinik'),
    };
  }
  if (!opts.phone.trim()) {
    return { available: false, reason: t('sms.no_phone', 'Patienten har intet telefonnummer.') };
  }
  if (!opts.consentAccepted) {
    return {
      available: false,
      reason: t('patients.communication.sms_needs_consent', 'SMS kræver at patienten er tilmeldt.'),
    };
  }
  if (!opts.hasCredit) {
    return {
      available: false,
      reason: t('sms.no_credit', 'Klinikken har ingen SMS-kredit. Køb en pakke under Indstillinger → SMS.'),
    };
  }
  return { available: true, reason: null };
}

function emailAvailability(
  t: (key: string, fallback: string) => string,
  opts: { canEmail: boolean; email: string; clinicEmailReady: boolean },
): ChannelAvailability {
  if (!opts.canEmail) return { available: false, reason: null };
  if (!opts.email.trim()) {
    return {
      available: false,
      reason: t('email.no_email_registered', 'Ingen email registreret'),
    };
  }
  if (!opts.clinicEmailReady) {
    return {
      available: false,
      reason: t(
        'patients.communication.email_needs_clinic_address',
        'Klinikken mangler en @clinio.app-adresse under Indstillinger → E-mail.',
      ),
    };
  }
  return { available: true, reason: null };
}

function messageAvailability(
  t: (key: string, fallback: string) => string,
  opts: { canMessages: boolean; hasPortalAccount: boolean },
): ChannelAvailability {
  if (!opts.canMessages) return { available: false, reason: null };
  if (!opts.hasPortalAccount) {
    return {
      available: false,
      reason: t(
        'patients.communication.no_portal',
        'Patienten har ingen portal-konto, så der kan ikke sendes beskeder.',
      ),
    };
  }
  return { available: true, reason: null };
}

function firstComposeChannel(
  availability: Record<CommunicationChannel, ChannelAvailability>,
  can: { canSms: boolean; canEmail: boolean; canMessages: boolean },
): CommunicationChannel {
  if (availability.sms.available) return 'sms';
  if (availability.email.available) return 'email';
  if (availability.messages.available) return 'messages';
  if (can.canSms) return 'sms';
  if (can.canEmail) return 'email';
  if (can.canMessages) return 'messages';
  return 'sms';
}

function resolveComposeChannel(
  preferred: CommunicationFilter,
  can: { canSms: boolean; canEmail: boolean; canMessages: boolean },
  fallback: CommunicationChannel,
): CommunicationChannel {
  if (preferred === 'sms' && can.canSms) return 'sms';
  if (preferred === 'email' && can.canEmail) return 'email';
  if (preferred === 'messages' && can.canMessages) return 'messages';
  return fallback;
}
